import { Chip, CircularProgress, Typography } from "@mui/material";
import { useRecordContext } from "react-admin";
import { Context, useContext } from "react";

type StatsContextValue<TEntry> = {
  statsMap: Map<number, TEntry>;
  isLoading: boolean;
};

type StatsFieldProps = {
  window: string;
  label?: string;
  source?: string;
  sortable?: boolean;
};

/**
 * Creates a StatsField component bound to a specific stats context.
 *
 * `renderEntry` receives the entry and window key, and returns either
 * `{ label, color }` for a Chip or `null` for a dash.
 */
export function createStatsField<TEntry>(
  StatsContext: Context<StatsContextValue<TEntry>>,
  renderEntry: (
    entry: TEntry,
    window: string
  ) => { label: string; color: "secondary" | "primary" } | null
): React.FC<StatsFieldProps> {
  const StatsField: React.FC<StatsFieldProps> = ({ window: statsWindow }) => {
    const record = useRecordContext();
    const { statsMap, isLoading } = useContext(StatsContext);

    if (isLoading) {
      return <CircularProgress size={16} />;
    }

    const entry =
      record?.id != null ? statsMap.get(Number(record.id)) : undefined;

    if (!entry) {
      return <Typography variant="body2">&ndash;</Typography>;
    }

    const result = renderEntry(entry, statsWindow);

    if (!result) {
      return <Typography variant="body2">&ndash;</Typography>;
    }

    return <Chip label={result.label} size="small" color={result.color} />;
  };

  return StatsField;
}
